/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/1152g05j/screen/67851869
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import path from 'ramda/src/path';
import moment from 'moment';
import Button from '@components/@core/AnimatedButton';
import DateRangePicker from '@components/@core/DateRangePicker';
import Dropdown from '@components/@core/Dropdown';
import Input from '@components/@core/Input';
import ProgressBar from '@components/@core/ProgressBar';
import Rating from '@components/@core/Rating';
import AppActions from '~/actions/app';
import CompanyActions from '~/actions/company';
import VendorActions from '~/actions/vendor';
import RatingCard from './RatingCard';

const Recent = ({ data, company, businessId }) => {
    const dispatch = useDispatch();
    const location = useLocation();

    const loginUser = useSelector((state) => state.app.user?.community_user);
    const companies = useSelector((state) => state.vendor.allVRCompanyInfo);

    const start = moment().subtract(2, 'years').format('MM/DD/YYYY');
    const end = moment().format('MM/DD/YYYY');
    const [date, setDate] = useState(`${start}-${end}`);
    const [rating, setRating] = useState(0);
    const [recommend, setRecommend] = useState(0);
    const [title, setTitle] = useState('');
    const [pros, setPros] = useState('');
    const [cons, setCons] = useState('');

    useEffect(() => {
        const payload = {
            type: 'Rate Vendor',
            data: {
                before: { company: data?.parent_id, category: data?.data?.category, product: data?.id },
            },
        };
        dispatch(VendorActions.getallvendorcompaniesRequest({ payload }));
    }, []);

    const handleOpenRating = () => {
        const payload = {
            type: 'Rate Vendor',
            data: {
                before: { company: data?.parent_id, category: data?.data?.category, product: data?.id },
            },
        };
        if (loginUser?.id) {
            if (String(loginUser?.profile?.member_type) !== '0') {
                toast.warn('Sorry, only dealers can leave reviews.', {
                    position: toast.POSITION.TOP_RIGHT,
                    pauseOnFocusLoss: false,
                    hideProgressBar: true,
                });
                return;
            }
            const after = { type: 'MODAL_REQUEST', payload };
            dispatch(VendorActions.getallvendorcompaniesRequest({ after }));
        } else {
            const route = `${location.pathname}${location.search}`;
            const after = { type: 'MODAL_REQUEST', payload };
            dispatch(
                AppActions.modalRequest({
                    type: 'Authentication',
                    data: { before: { route, after } },
                })
            );
        }
    };

    const handleDates = (e) => {
        const start = moment(e.split('-')[0]).format('YYYY-MM-DD');
        const end = moment(e.split('-')[1]).format('YYYY-MM-DD');
        dispatch(CompanyActions.getbusinessRequest({ id: businessId, date_start: start, date_end: end }));
    };

    const handleGetBusiness = () => {
        dispatch(CompanyActions.getbusinessRequest({ id: businessId }));
    };

    const RecommendSymbol = [...Array(10)].map((item, idx) => {
        let styleClass = `rate-vendor-modal-recommend-rate`;
        if (idx + 1 === recommend) styleClass = `active ${styleClass}`;
        return (
            <div key={`rating-${idx}`} className={styleClass}>
                {idx + 1}
            </div>
        );
    });

    const handleSubmit = () => {
        if (String(loginUser?.profile?.member_type) !== '0') {
            toast.warn('Sorry, only dealers can leave reviews.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        const phone = loginUser?.profile?.contact_phone;
        const payload = {
            product_id: data.id,
            summary: title,
            pros,
            cons,
            phone,
            likely: recommend,
            star_rating: rating,
            product_name: data.name,
        };
        dispatch(VendorActions.savevendorratingRequest(payload));
    };

    const disabled = useMemo(() => {
        return title === '' || rating === 0 || recommend === 0 || pros === '' || cons === '';
    }, [title, rating, recommend, pros, cons]);

    return (
        <>
            <DateRangePicker className="ml-auto mb-3" as="span" selected={date} onChange={handleDates} />
            <div className="d-flex justify-content-between border-bottom pb-4">
                <span className="dsl-b24 bold">Recent Verified Ratings</span>
                {data.ratings?.length > 0 && <Button name="RATE IT" onClick={handleOpenRating} />}
            </div>
            {data.ratings?.length > 0 ? (
                <div className="d-flex flex-md-row flex-column justify-content-between border-bottom mt-3 pb-3">
                    <div>
                        <p className="dsl-b20 bold">Star Rating Distribution</p>
                        <div className="d-flex">
                            <Rating score={5} />
                            <span className="dsl-b16 text-400 ms-2">{path(['ratings_breakdown', '5-stars', 'count'], data)}</span>
                        </div>
                        <div className="d-flex mt-2">
                            <Rating score={4} />
                            <span className="dsl-b16 text-400 ms-2">{path(['ratings_breakdown', '4-stars', 'count'], data)}</span>
                        </div>
                        <div className="d-flex mt-2">
                            <Rating score={3} />
                            <span className="dsl-b16 text-400 ms-2">{path(['ratings_breakdown', '3-stars', 'count'], data)}</span>
                        </div>
                        <div className="d-flex mt-2">
                            <Rating score={2} />
                            <span className="dsl-b16 text-400 ms-2">{path(['ratings_breakdown', '2-stars', 'count'], data)}</span>
                        </div>
                        <div className="d-flex mt-2">
                            <Rating score={1} />
                            <span className="dsl-b16 text-400 ms-2">{path(['ratings_breakdown', '1-stars', 'count'], data)}</span>
                        </div>
                    </div>
                    <div className="rating-by mt-3 mt-md-0">
                        <p className="dsl-b20 bold">Rating by Job Title</p>
                        {data?.ratings_by_job &&
                            data?.ratings_by_job.map((job, idx) => <ProgressBar key={`job-${job.job}-${idx}`} className="rating-by mb-2" title={job.job} value={job.percent} horizontal />)}
                    </div>
                </div>
            ) : (
                <div className="p-3">
                    <Dropdown disabled className="py-3" title="Select company" width="fit-content" direction="vertical" data={companies} defaultIds={[company.id]} getValue={(e) => e?.name} />
                    <Dropdown className="py-3" title="Select a product" width="fit-content" direction="vertical" data={company.products ?? []} defaultIds={[data.id]} getValue={(e) => e?.name} />
                    <Rating className="py-3" title="Rate product" direction="vertical" readonly={false} score={rating} size="large" fractions={2} onChange={(e) => setRating(e)} />
                    <Rating
                        className="py-3"
                        title="Likely to recommend?"
                        direction="vertical"
                        readonly={false}
                        showScore={false}
                        score={recommend}
                        size="large"
                        topRate={10}
                        empty={RecommendSymbol}
                        full={RecommendSymbol}
                        feedback={['Poor', 'Good', 'Excellent']}
                        onChange={(e) => setRecommend(e)}
                    />
                    <Input className="py-3" title="Preview Title" placeholder="Type here..." direction="vertical" value={title} required onChange={(e) => setTitle(e)} />
                    <Input className="py-3" title="Pros" placeholder="Type here..." direction="vertical" rows={3} as="textarea" value={pros} required onChange={(e) => setPros(e)} />
                    <Input title="Cons" className="py-3" rows={3} direction="vertical" as="textarea" placeholder="Type here..." value={cons} required onChange={(e) => setCons(e)} />
                    <p className="required-label dsl-m14">* Required</p>
                    <div className="d-flex jc-end ssm-jc-center">
                        <Button disabled={disabled} name="Submit" onClick={handleSubmit} />
                    </div>
                </div>
            )}
            <RatingCard ratings={data?.ratings} callback={handleGetBusiness} />
        </>
    );
};

Recent.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    }),
};

Recent.defaultProps = {
    data: {
        id: 0,
        name: '',
    },
};

export default Recent;
